import React from 'react';
import { Namespace } from '../../../../../translation/namespace';
import { translateWithCustomTransMarker } from '../../../../../lib/translation/utils';
import { Heading } from '../../../../../_lib/components/Heading/Heading';
import {
  Input,
  Option,
  RequiredHelpPhrase,
  Row,
  Select,
  SelectParam,
  Textarea,
  TextAreaParam,
  TextParam,
} from '../../../../../_lib/Form2';
import { PageType } from '../../../../../types/api/PageType';
import { useSonTranslation } from '../../../../../translation/useSonTranslation';
import { formParamsUtils } from '../../../../../_lib/Form2/utils/formParamsUtils';

type ReadRestrictionOption = { value: 'inheritFromParent' | 'membershipToOwner'; text: string };
type ConfidentialLevelOption = { value: 'open' | 'officialUse' | 'confidential' | 'secret'; text: string };

export type PageInformationParamType = ReturnType<typeof formParamsUtils<
  TextAreaParam<'leadText'>
  | TextParam<'title'>
  | SelectParam<'confidentialLevel', ConfidentialLevelOption>
  | SelectParam<'initialReadRestriction', ReadRestrictionOption>>>;

type PageInformationType = {
  pageType?: PageType;
  params: PageInformationParamType
};

export function PageInformation({ pageType, params }: PageInformationType) {
  const { t } = useSonTranslation('CreatePage');

  if (!pageType || !params.anyParameters()) {
    return null;
  }

  const title = params.findByName('title');
  const leadText = params.findByName('leadText');
  const readRestriction = params.findByName('initialReadRestriction');
  const confidentialLevel = params.findByName('confidentialLevel');

  return (
    <>
      <Heading className="create-page-form__heading font-bold-16-20" hierarchy={3}>
        {t(`${Namespace.PageManagement}:views.create-page-general.headline`, { page: pageType })}
      </Heading>
      <RequiredHelpPhrase />
      {title?.name && (
        <Row>
          <Input label={t(`${Namespace.glossary}:title`)} param={title} />
        </Row>
      )}
      {leadText?.name && (
        <Row>
          <Textarea autoExpand label={t(`${Namespace.glossary}:abstract`)} param={leadText} />
        </Row>
      )}
      {readRestriction?.name && (
        <Row>
          <Select label={t(`${Namespace.phrases}:readRestriction`)} param={readRestriction}>
            {readRestriction.options.map(option => (
              <Option
                key={option.value}
                label={t(
                  `PageManagement:views.create-page-general.inputs.readRestriction.options.${option.value}.text`,
                )}
                value={option.value}
              >
                {t(`PageManagement:views.create-page-general.inputs.readRestriction.options.${option.value}.text`)}
              </Option>
            ))}
          </Select>
        </Row>
      )}
      {confidentialLevel?.name && (
        <Row>
          <Select
            label={translateWithCustomTransMarker(t(`${Namespace.glossary}:classification`))}
            param={confidentialLevel}
          >
            {confidentialLevel.options.map(option => (
              <Option
                key={option.value}
                label={t(
                  `PageManagement:views.create-page-general.inputs.confidentialLevel.options.${option.value}.text`,
                )}
                value={option.value}
              >
                {t(`PageManagement:views.create-page-general.inputs.confidentialLevel.options.${option.value}.text`)}
              </Option>
            ))}
          </Select>
        </Row>
      )}
    </>
  );
}
